import {ImageProps, Image, CenteredGrid} from '../';
import {WithCaptionProps} from '../../wraps';
import React, {useState, useCallback, useEffect} from 'react';
import styled from 'styled-components';
import Carousel from 'nuka-carousel';
import {ArrowControls} from '../../layout/features/FeaturesCarousel.controls';
import {useBreakpointDown, useCurrentAccentColor} from 'epic-ue-theme';
import Grid from '@material-ui/core/Grid';
import {GridContainer, VerticalSpacer, Typography} from '../';
import {AccentColorOptions} from 'epic-ue-theme/src/types/colors.types';

const ArticleCarouselWrapper = styled.div<{afterRender: boolean}>`
    position: relative;
    overflow: visible;

    .h2 {
        text-align: center;
    }

    .slider {
        .slider-slide {
            opacity: 0.35;
            pointer-events: none;
            user-select: none;
            &.slide-current {
                opacity: 1;
                transition: opacity 0.25s ${(p) => p.theme.easing};
            }
        }

        li {
            & > span,
            & > a {
                display: block;
                height: ${(p) => (p.afterRender ? '100%' : '')};
            }
        }

        .ue-carousel-btn.disabled {
            pointer-events: none;
        }

        .carousel-slide-caption {
            position: relative;
            right: 0;
            bottom: 0;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding: 8px;
        }
    }
`;

const SlideCount = styled.div`
    color: ${(p) => p.theme.palette.text.primary};
    user-select: none;
    display: inline-block;
`;

interface SlideProps {
    caption?: WithCaptionProps;
    image?: ImageProps;
}

export interface ArticleCarouselProps {
    slides?: SlideProps[];
    title?: string;
    copy?: string;
    accentColor?: AccentColorOptions;
}

export const ArticleCarousel = ({
    slides = [],
    title = '',
    accentColor
}: ArticleCarouselProps): JSX.Element => {
    const slideAmount = slides.length || 0;
    const isXs = useBreakpointDown('xs');
    const isSm = useBreakpointDown('sm');
    const cellSpacing = isSm || isXs ? 20 : 250;
    const currentAccentColor = useCurrentAccentColor();

    const [slideIndex, setSlideIndex] = useState(0);
    const beforeSlideCallback = useCallback(
        (_, nextIndex) => {
            if (slideIndex !== nextIndex) {
                setSlideIndex(nextIndex);
            }
        },
        [setSlideIndex, slideIndex]
    );

    const [afterRender, setAfterRender] = useState(false);
    useEffect(() => {
        // Wait until Nuka carousel renders the initial slide height, then apply new height style based on state
        if (!afterRender) {
            setTimeout(function () {
                setAfterRender(true);
            }, 100);
        }
    }, [setAfterRender, afterRender]);

    const leftArrowDisable = slideIndex === 0;
    const rightArrowDisable = slideIndex === slides.length - 1;

    return (
        <ArticleCarouselWrapper afterRender={afterRender}>
            <GridContainer>
                <CenteredGrid>
                    <Grid item xs={12}>
                        {title && (
                            <>
                                <Typography variant="h2"> {title}</Typography>
                                <VerticalSpacer variant={'heading'} />
                            </>
                        )}
                    </Grid>
                </CenteredGrid>

                <Carousel
                    heightMode="max"
                    easing="easeExpOut"
                    dragging={false}
                    enableKeyboardControls
                    cellSpacing={cellSpacing}
                    cellAlign="center"
                    slideIndex={slideIndex}
                    framePadding={`0 100px`}
                    slidesToShow={1}
                    slidesToScroll={1}
                    beforeSlide={beforeSlideCallback}
                    renderBottomCenterControls={null}
                    renderCenterLeftControls={({previousSlide}) => (
                        <ArrowControls
                            onClick={previousSlide}
                            disabled={leftArrowDisable}
                            type="left"
                            accentColor={accentColor || currentAccentColor.name}
                        />
                    )}
                    renderCenterRightControls={({nextSlide}) => (
                        <ArrowControls
                            onClick={nextSlide}
                            disabled={rightArrowDisable}
                            type="right"
                            accentColor={accentColor || currentAccentColor.name}
                        />
                    )}>
                    {slides.map((slide = {}, i) => {
                        const slideCaption = slide.caption || {};
                        return (
                            <div key={`article-slide-${i}`}>
                                <Image fit={'contain'} {...slide.image} />
                                <div className="carousel-slide-caption">
                                    <SlideCount>
                                        <Typography variant="h4">
                                            {slideIndex + 1} {'/'} {slideAmount}
                                        </Typography>
                                    </SlideCount>
                                    <Typography variant="p" caption>
                                        {slideCaption.text || ''}
                                    </Typography>
                                </div>
                            </div>
                        );
                    })}
                </Carousel>
            </GridContainer>
        </ArticleCarouselWrapper>
    );
};
